/**
 * The Analytics line chart: four daily series on one axis.
 *
 * Pure input->HTML, like kb_read_heatmap.ts: no fetching, no module state, no
 * DOM. That is what makes the axis maths and the day bucketing cheap to pin in
 * a test with a fixed dataset and a fixed "today".
 *
 * ── Why one chart and not four ───────────────────────────────────────────
 * Reads and writes on separate axes cannot be compared, and comparison is the
 * whole point: a knowledge base only ever written to is a write-only log. On a
 * shared axis the ratio is visible at a glance. Splitting one heavy reader out
 * matters for the same reason - their volume otherwise swamps everyone else's
 * line and the chart answers nothing about the team.
 *
 * ── Why SVG rather than Chart.js ─────────────────────────────────────────
 * chart.js IS already on the page from a CDN, so it would cost no bytes. It
 * draws to a canvas through an imperative API, so nothing about the result can
 * be asserted without a browser - and the correctness that matters here is
 * exactly what a screenshot cannot show: that a quiet day is a gap in the line
 * rather than a missing point that silently shortens the axis.
 *
 * CSS: styles/views/kb-analytics.css
 */

import { esc } from '../../shared/rendering.js'
import { addDays, longDate, todayUTC, utcDay } from './kb_read_heatmap.js'

export type DailyPoint = { readonly day: string; readonly value: number }

/** One line: its points, its colour token, and what to call it. */
export type Series = {
  readonly key: string
  readonly label: string
  /** Accent token from tokens.css, resolved through var(). */
  readonly colour: string
  readonly points: readonly DailyPoint[]
  /** Dashed lines read as "the same measure, a different subject". */
  readonly dashed?: boolean
}

const VIEW_W = 960
const VIEW_H = 300
const PAD_LEFT = 46
const PAD_RIGHT = 14
const PAD_TOP = 14
const PAD_BOTTOM = 38
/** Roughly how many x labels fit without overprinting at this width. */
const MAX_X_LABELS = 10

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-08-17` -> `17-Aug`. */
export function shortDayLabel(day: string): string {
  const [, m, d] = day.split('-')
  const month = MONTHS[Number(m) - 1]
  if (!month || !d) return day
  return `${d}-${month}`
}

/**
 * Expand a sparse server series into exactly one point per day.
 *
 * The server returns only days that had activity. Plotting those directly would
 * squeeze a quiet fortnight into the same width as a busy one, so the line
 * would show a steady rhythm where the truth is a gap - the most interesting
 * thing a per-day chart can tell you. One point per day keeps the x-axis a real
 * timeline, and is what makes a single point sit on a single day.
 */
export function fillDays(
  rows: readonly DailyPoint[],
  days: number,
  today: Date = todayUTC(),
): DailyPoint[] {
  const have = new Map<string, number>()
  for (const r of rows) have.set(r.day, (have.get(r.day) ?? 0) + r.value)
  const out: DailyPoint[] = []
  for (let i = days - 1; i >= 0; i--) {
    const day = utcDay(addDays(today, -i))
    out.push({ day, value: have.get(day) ?? 0 })
  }
  return out
}

/**
 * A round number at or above `max`, for the top grid line.
 *
 * Floor of 4 so the three grid labels stay distinct: with a top of 1 the middle
 * label rounds to 1 as well and the axis reads "0, 1, 1".
 */
export function niceMax(max: number): number {
  if (max <= 4) return 4
  const mag = Math.pow(10, Math.floor(Math.log10(max)))
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * mag
    if (candidate >= max) return candidate
  }
  return 10 * mag
}

/**
 * Indices to label on the x-axis, evenly spaced, always including the last day.
 *
 * A tick per day is unreadable past about a fortnight, and at 365 there
 * is no room for them at all,
 * so the labels thin out while every point keeps its own hover
 * target - the axis is for orientation, the tooltip is for values.
 */
export function labelIndices(count: number, max = MAX_X_LABELS): number[] {
  if (count <= 0) return []
  if (count <= max) return Array.from({ length: count }, (_, i) => i)
  const step = (count - 1) / (max - 1)
  const out = new Set<number>()
  for (let i = 0; i < max; i++) out.add(Math.round(i * step))
  out.add(count - 1)
  return [...out].sort((a, b) => a - b)
}

/**
 * A grid label that matches the line it sits on.
 *
 * Rounding was wrong: with a top of 25 the middle grid line is at 12.5 and was
 * labelled "13", so the axis claimed a value the line did not represent. One
 * decimal only when the value genuinely has one.
 */
export function axisLabel(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

export function seriesMax(series: readonly Series[]): number {
  let m = 0
  for (const s of series) for (const p of s.points) if (p.value > m) m = p.value
  return m
}

function plotGeometry(count: number) {
  const plotW = VIEW_W - PAD_LEFT - PAD_RIGHT
  const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM
  // Points sit ON grid positions rather than in the middle of a band: a
  // line chart connects instants, not buckets of width.
  const dx = count > 1 ? plotW / (count - 1) : 0
  return { plotW, plotH, dx }
}

function pointX(i: number, dx: number, count: number): number {
  return count > 1 ? PAD_LEFT + i * dx : PAD_LEFT + (VIEW_W - PAD_LEFT - PAD_RIGHT) / 2
}

function pointY(value: number, top: number, plotH: number): number {
  return PAD_TOP + plotH - (value / top) * plotH
}

function renderLine(s: Series, top: number, count: number): string {
  const { plotH, dx } = plotGeometry(count)
  const d = s.points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${pointX(i, dx, count).toFixed(1)} ${pointY(p.value, top, plotH).toFixed(1)}`,
    )
    .join(' ')
  return `<path class="kl-line${s.dashed ? ' kl-dashed' : ''}" d="${d}" stroke="var(--${esc(s.colour)})" fill="none"/>`
}

/**
 * Largest hover radius that cannot reach the neighbouring day.
 *
 * A fixed `r` in USER units was the bug: at 365 days the spacing is 2.47 units
 * and r was 7, so a point's hit area covered its neighbours and DOM order
 * decided the answer. 314 of 365 points reported the wrong DAY. Capped at just
 * under half the spacing, it cannot.
 */
export function hitRadius(count: number): number {
  const { dx } = plotGeometry(count)
  if (count <= 1) return 9
  return Math.max(0.9, Math.min(7, dx * 0.45))
}

/**
 * Hover targets: one circle per day per series.
 *
 * The label rides on a data attribute and a delegated listener renders it, NOT
 * on an SVG `<title>`. A `<title>` is free and needs no JavaScript, but the
 * browser shows it as an OS tooltip after about a second - too slow to sweep a
 * line with, which is the actual gesture here. Same custom-tooltip approach the
 * graph view already uses, so hovering behaves the same in both.
 *
 * `r` stays small while the invisible hit area is larger, so a 365-point line
 * can still be pointed at.
 */
function renderPoints(s: Series, top: number, count: number, unit: Unit): string {
  const { plotH, dx } = plotGeometry(count)
  // Dots for every day stop being dots past a couple of months; the line
  // carries the shape by then and the hit targets carry the values.
  const showDots = count <= 120
  return s.points
    .map((p, i) => {
      const x = pointX(i, dx, count).toFixed(1)
      const y = pointY(p.value, top, plotH).toFixed(1)
      const noun = p.value === 1 ? unit.one : unit.many
      const label = `${s.label}: ${String(p.value)} ${noun} on ${longDate(p.day)}`
      return (
        (showDots
          ? `<circle class="kl-dot" cx="${x}" cy="${y}" r="2.4" fill="var(--${esc(s.colour)})"/>`
          : '') +
        `<circle class="kl-hit" cx="${x}" cy="${y}" r="${hitRadius(count).toFixed(2)}" data-label="${esc(label)}"` +
        ` data-colour="${esc(s.colour)}"></circle>`
      )
    })
    .join('')
}

/**
 * Singular and plural given, not derived. "1 reads" is the kind of thing nobody
 * notices in review and everybody notices on screen, and appending an "s" to
 * "entry" is simply wrong.
 */
type Unit = { readonly one: string; readonly many: string }

const READS: Unit = { one: 'read', many: 'reads' }
const WRITES: Unit = { one: 'entry', many: 'entries' }

export type ChartOptions = {
  readonly series: readonly Series[]
  /** Days in the window; every series must already be filled to this length. */
  readonly days: number
}

/**
 * The whole chart: legend, grid, four lines, hover targets.
 *
 * The SVG carries a summary aria-label naming each series and its total. There
 * is deliberately no data table - it was removed on request - so this label is
 * the only thing a screen reader gets, and it has to say something useful.
 */
export function renderAnalyticsChart(opts: ChartOptions): string {
  const series = opts.series
  const count = series[0]?.points.length ?? 0
  const top = niceMax(seriesMax(series))
  const { plotH, dx } = plotGeometry(count)

  const totals = series.map((s) => ({
    label: s.label,
    total: s.points.reduce((n, p) => n + p.value, 0),
  }))

  const legend = series
    .map(
      (s, i) =>
        `<span class="kl-key"><i class="kl-swatch${s.dashed ? ' kl-swatch-dashed' : ''}" style="background:var(--${esc(s.colour)})"></i>${esc(s.label)} <b>${String(totals[i]?.total ?? 0)}</b></span>`,
    )
    .join('')

  const grid = [0, 0.5, 1]
    .map((f) => {
      const y = PAD_TOP + plotH * (1 - f)
      return `<line class="kl-grid" x1="${PAD_LEFT}" y1="${y.toFixed(1)}" x2="${VIEW_W - PAD_RIGHT}" y2="${y.toFixed(1)}"/>
        <text class="kl-y" x="${PAD_LEFT - 8}" y="${(y + 4).toFixed(1)}">${axisLabel(top * f)}</text>`
    })
    .join('')

  const xLabels = labelIndices(count)
    .map((i) => {
      const p = series[0]?.points[i]
      if (!p) return ''
      const x = pointX(i, dx, count)
      // The last label is right-anchored so it cannot overhang the axis.
      const anchor = i === count - 1 ? 'end' : i === 0 ? 'start' : 'middle'
      return `<text class="kl-x" x="${x.toFixed(1)}" y="${String(VIEW_H - 14)}" text-anchor="${anchor}">${esc(shortDayLabel(p.day))}</text>`
    })
    .join('')

  // Built as a plain string first: interpolating it inside the attribute would
  // nest a template expression that ST-aria-label cannot see through.
  const summary = totals.map((t) => `${t.label} ${String(t.total)}`).join(', ')
  const ariaLabel = `Daily knowledge base activity over ${String(opts.days)} days. Totals: ${summary}.`

  return `<section class="kl-chart kl-stage">
    <div class="kl-legend" role="group" aria-label="Series">${legend}</div>
    <svg class="kl-svg" viewBox="0 0 ${String(VIEW_W)} ${String(VIEW_H)}"
         role="img" aria-label="${esc(ariaLabel)}">
      ${grid}
      ${series.map((s) => renderLine(s, top, count)).join('')}
      ${series.map((s) => renderPoints(s, top, count, s.key.startsWith('reads') ? READS : WRITES)).join('')}
      ${xLabels}
    </svg>
    <div class="kl-tip" hidden></div>
  </section>`
}
